"use client";

import { CaretUpDown, MagnifyingGlass } from "@phosphor-icons/react";
import { Command as CommandPrimitive } from "cmdk";
import type { ComponentProps, ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { CommandDialog } from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Icon } from "@/lib/icon";
import { cn } from "@/lib/utils";

export type ModelSelectorProps = ComponentProps<typeof Popover>;

export const ModelSelector = (props: ModelSelectorProps) => (
  <Popover {...props} />
);

export type ModelSelectorTriggerProps = ComponentProps<typeof Button>;

export const ModelSelectorTrigger = ({
  variant = "outline",
  size = "default",
  className,
  children,
  ...props
}: ModelSelectorTriggerProps) => (
  <PopoverTrigger
    render={
      <Button
        className={cn("gap-2 rounded-md", className)}
        size={size}
        variant={variant}
        {...props}
      >
        {children}
        <Icon
          aria-hidden="true"
          className="text-muted-foreground"
          icon={CaretUpDown}
          size={12}
        />
      </Button>
    }
  />
);

ModelSelectorTrigger.displayName = "ModelSelectorTrigger";

export type ModelSelectorContentProps = ComponentProps<
  typeof PopoverContent
> & {
  title?: ReactNode;
};

export const ModelSelectorContent = ({
  align = "start",
  className,
  children,
  sideOffset = 6,
  title = "Select model",
  ...props
}: ModelSelectorContentProps) => (
  <PopoverContent
    align={align}
    className={cn(
      "w-70 gap-0 rounded-lg border border-border bg-popover p-0 text-popover-foreground shadow-[0_4px_12px_-2px_rgba(0,0,0,0.1)] ring-0",
      className,
    )}
    sideOffset={sideOffset}
    {...props}
  >
    <CommandPrimitive className="flex flex-col">
      {title ? <ModelSelectorHeader>{title}</ModelSelectorHeader> : null}
      {children}
    </CommandPrimitive>
  </PopoverContent>
);

ModelSelectorContent.displayName = "ModelSelectorContent";

export type ModelSelectorHeaderProps = ComponentProps<"div">;

export const ModelSelectorHeader = ({
  className,
  ...props
}: ModelSelectorHeaderProps) => (
  <div
    data-slot="model-selector-header"
    className={cn(
      "flex items-start px-3 pt-3 pb-1 font-semibold text-popover-foreground text-sm",
      className,
    )}
    {...props}
  />
);

ModelSelectorHeader.displayName = "ModelSelectorHeader";

export type ModelSelectorInputProps = ComponentProps<
  typeof CommandPrimitive.Input
>;

export const ModelSelectorInput = ({
  className,
  ...props
}: ModelSelectorInputProps) => (
  <div className="flex items-center gap-2 border-border border-b px-3 py-2.5">
    <Icon
      aria-hidden="true"
      className="text-muted-foreground"
      icon={MagnifyingGlass}
      size={12}
    />
    <CommandPrimitive.Input
      className={cn(
        "w-full bg-transparent font-normal text-popover-foreground text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
      data-slot="model-selector-input"
      {...props}
    />
  </div>
);

ModelSelectorInput.displayName = "ModelSelectorInput";

export type ModelSelectorListProps = ComponentProps<
  typeof CommandPrimitive.List
>;

export const ModelSelectorList = ({
  className,
  ...props
}: ModelSelectorListProps) => (
  <CommandPrimitive.List
    className={cn(
      "no-scrollbar flex max-h-72 flex-col gap-1 overflow-x-hidden overflow-y-auto p-1 outline-none",
      className,
    )}
    data-slot="model-selector-list"
    {...props}
  />
);

ModelSelectorList.displayName = "ModelSelectorList";

export type ModelSelectorEmptyProps = ComponentProps<
  typeof CommandPrimitive.Empty
>;

export const ModelSelectorEmpty = ({
  className,
  ...props
}: ModelSelectorEmptyProps) => (
  <CommandPrimitive.Empty
    className={cn(
      "py-6 text-center font-normal text-muted-foreground text-sm",
      className,
    )}
    data-slot="model-selector-empty"
    {...props}
  />
);

ModelSelectorEmpty.displayName = "ModelSelectorEmpty";

export type ModelSelectorGroupProps = ComponentProps<
  typeof CommandPrimitive.Group
>;

export const ModelSelectorGroup = ({
  className,
  ...props
}: ModelSelectorGroupProps) => (
  <CommandPrimitive.Group
    className={cn(
      "flex flex-col gap-0.5 overflow-hidden **:[[cmdk-group-heading]]:px-2 **:[[cmdk-group-heading]]:pt-1.5 **:[[cmdk-group-heading]]:pb-0.5 **:[[cmdk-group-heading]]:font-medium **:[[cmdk-group-heading]]:text-muted-foreground **:[[cmdk-group-heading]]:text-xs",
      className,
    )}
    data-slot="model-selector-group"
    {...props}
  />
);

ModelSelectorGroup.displayName = "ModelSelectorGroup";

export type ModelSelectorItemProps = ComponentProps<
  typeof CommandPrimitive.Item
>;

export const ModelSelectorItem = ({
  className,
  ...props
}: ModelSelectorItemProps) => (
  <CommandPrimitive.Item
    className={cn(
      "flex w-full cursor-default items-center gap-2 rounded-sm px-2 py-1.5 font-normal text-popover-foreground text-sm outline-none select-none data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50 data-selected:bg-accent data-selected:text-accent-foreground [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
      className,
    )}
    data-slot="model-selector-item"
    {...props}
  />
);

ModelSelectorItem.displayName = "ModelSelectorItem";

export type ModelSelectorSeparatorProps = ComponentProps<
  typeof CommandPrimitive.Separator
>;

export const ModelSelectorSeparator = ({
  className,
  ...props
}: ModelSelectorSeparatorProps) => (
  <CommandPrimitive.Separator
    aria-hidden="true"
    className={cn("-mx-1 my-1 h-px bg-border", className)}
    data-slot="model-selector-separator"
    role="none"
    {...props}
  />
);

ModelSelectorSeparator.displayName = "ModelSelectorSeparator";

export type ModelSelectorShortcutProps = ComponentProps<"span">;

export const ModelSelectorShortcut = ({
  className,
  ...props
}: ModelSelectorShortcutProps) => (
  <span
    className={cn(
      "ml-auto font-normal text-muted-foreground text-xs tracking-widest",
      className,
    )}
    data-slot="model-selector-shortcut"
    {...props}
  />
);

ModelSelectorShortcut.displayName = "ModelSelectorShortcut";

// Full-screen command-palette flavor kept available for settings surfaces
// that prefer the modal palette UX over the inline anchored picker.
export type ModelSelectorDialogProps = ComponentProps<typeof CommandDialog>;

export const ModelSelectorDialog = (props: ModelSelectorDialogProps) => (
  <CommandDialog {...props} />
);

ModelSelectorDialog.displayName = "ModelSelectorDialog";

export type ModelSelectorLogoProps = Omit<
  ComponentProps<"img">,
  "src" | "alt"
> & {
  provider:
    | "moonshotai-cn"
    | "lucidquery"
    | "moonshotai"
    | "zai-coding-plan"
    | "alibaba"
    | "xai"
    | "vultr"
    | "nvidia"
    | "upstage"
    | "groq"
    | "github-copilot"
    | "mistral"
    | "vercel"
    | "nebius"
    | "deepseek"
    | "alibaba-cn"
    | "google-vertex-anthropic"
    | "venice"
    | "chutes"
    | "cortecs"
    | "github-models"
    | "togetherai"
    | "azure"
    | "baseten"
    | "huggingface"
    | "opencode"
    | "fastrouter"
    | "google"
    | "google-vertex"
    | "cloudflare-workers-ai"
    | "inception"
    | "wandb"
    | "openai"
    | "zhipuai-coding-plan"
    | "perplexity"
    | "openrouter"
    | "zenmux"
    | "v0"
    | "iflowcn"
    | "synthetic"
    | "deepinfra"
    | "zhipuai"
    | "submodel"
    | "zai"
    | "inference"
    | "requesty"
    | "morph"
    | "lmstudio"
    | "anthropic"
    | "aihubmix"
    | "fireworks-ai"
    | "modelscope"
    | "llama"
    | "scaleway"
    | "amazon-bedrock"
    | "cerebras"
    // oxlint-disable-next-line typescript-eslint(ban-types) -- intentional pattern for autocomplete-friendly string union
    | (string & {});
};

export const ModelSelectorLogo = ({
  provider,
  className,
  ...props
}: ModelSelectorLogoProps) => (
  <img
    {...props}
    alt={`${provider} logo`}
    className={cn("size-3 dark:invert", className)}
    height={12}
    src={`https://models.dev/logos/${provider}.svg`}
    width={12}
  />
);

export type ModelSelectorLogoGroupProps = ComponentProps<"div">;

export const ModelSelectorLogoGroup = ({
  className,
  ...props
}: ModelSelectorLogoGroupProps) => (
  <div
    className={cn(
      "flex shrink-0 items-center -space-x-1 [&>img]:rounded-full [&>img]:bg-background [&>img]:p-px [&>img]:ring-1 dark:[&>img]:bg-foreground",
      className,
    )}
    {...props}
  />
);

export type ModelSelectorNameProps = ComponentProps<"span">;

export const ModelSelectorName = ({
  className,
  ...props
}: ModelSelectorNameProps) => (
  <span className={cn("flex-1 truncate text-left", className)} {...props} />
);
